import type { CreateUniLoginHostedAuthRequestResponse } from '@unipile/types';

export type UniLoginBridgeAction = 'probe' | 'start' | 'status' | 'retry';

export type UniLoginBridge = {
  call(action: UniLoginBridgeAction, request?: CreateUniLoginHostedAuthRequestResponse): Promise<unknown>;
  dispose(): void;
};

export type ExtensionBrowser = 'chrome' | 'edge' | 'firefox' | 'safari';

/** Origins allowed to serve the extension bridge page, besides the page's own origin and local development. */
const UNILOGIN_BRIDGE_ORIGINS = ['https://unilogin.app', 'https://account.unilogin.app', 'https://auth.unilogin.app'];
const LOOPBACK_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]'];
const BRIDGE_READY_TIMEOUT_MS = 5_000;
const BRIDGE_LOAD_FALLBACK_MS = 500;
const BRIDGE_CALL_TIMEOUT_MS = { probe: 4_000, status: 8_000, start: 30_000, retry: 30_000 } satisfies Record<
  UniLoginBridgeAction,
  number
>;

/** What the extension reported through the bridge; `state` and `error` are extension codes. */
export type UniLoginExtensionUpdate =
  | { ok: true; state: string | null; error: string | null }
  | { ok: false; error: string | null };

/**
 * Talks to the UniLogin extension through the hidden bridge page (`extension_bridge_url`), which relays
 * postMessage requests to the extension. Only the bridge origin is trusted, and responses are matched by
 * correlation id.
 */
export function createUniLoginBridge(url: string): UniLoginBridge {
  const bridgeUrl = new URL(url);
  const iframe = document.createElement('iframe');
  iframe.hidden = true;
  iframe.setAttribute('aria-hidden', 'true');
  let disposed = false;
  let cancelReady = (): void => undefined;
  const pendingCalls = new Set<() => void>();
  const ready = new Promise<void>((resolve, reject) => {
    let settled = false;
    let loadFallbackTimer: number | undefined;
    const finish = (error?: Error): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.clearTimeout(loadFallbackTimer);
      window.removeEventListener('message', receive);
      iframe.removeEventListener('load', fallbackAfterLoad);
      if (error) reject(error);
      else resolve();
    };
    const receive = (event: MessageEvent<unknown>): void => {
      if (
        event.origin !== bridgeUrl.origin ||
        event.source !== iframe.contentWindow ||
        !isRecord(event.data) ||
        event.data.type !== 'unilogin:extension-bridge:ready'
      )
        return;
      finish();
    };
    const fallbackAfterLoad = (): void => {
      loadFallbackTimer = window.setTimeout(() => finish(), BRIDGE_LOAD_FALLBACK_MS);
    };
    const timer = window.setTimeout(() => finish(new Error('unilogin_bridge_timeout')), BRIDGE_READY_TIMEOUT_MS);
    cancelReady = () => finish(new Error('unilogin_bridge_disposed'));
    window.addEventListener('message', receive);
    iframe.addEventListener('load', fallbackAfterLoad, { once: true });
  });
  iframe.src = bridgeUrl.toString();
  document.body.appendChild(iframe);

  return {
    async call(action, request) {
      await ready;
      if (disposed) throw new Error('unilogin_bridge_disposed');
      const correlationId = crypto.randomUUID();
      return new Promise((resolve, reject) => {
        const settle = (): void => {
          window.clearTimeout(timer);
          window.removeEventListener('message', receive);
          pendingCalls.delete(cancel);
        };
        const cancel = (): void => {
          settle();
          reject(new Error('unilogin_bridge_disposed'));
        };
        const receive = (event: MessageEvent<unknown>): void => {
          if (event.origin !== bridgeUrl.origin || event.source !== iframe.contentWindow) return;
          if (!isBridgeResponse(event.data, correlationId)) return;
          settle();
          resolve(event.data.payload);
        };
        const timer = window.setTimeout(() => {
          settle();
          reject(new Error('unilogin_bridge_timeout'));
        }, BRIDGE_CALL_TIMEOUT_MS[action]);
        pendingCalls.add(cancel);
        window.addEventListener('message', receive);
        iframe.contentWindow?.postMessage(
          {
            type: 'unilogin:extension-bridge:request',
            correlationId,
            action,
            ...(request && {
              reconciliationId: request.request_id,
              ...(action === 'start' && { handoffToken: request.handoff_token }),
            }),
          },
          bridgeUrl.origin,
        );
      });
    },
    dispose() {
      disposed = true;
      cancelReady();
      for (const cancel of pendingCalls) cancel();
      iframe.remove();
    },
  };
}

/** Reads a bridge payload; anything unexpected is treated as a failure without error code. */
export function readExtensionUpdate(payload: unknown): UniLoginExtensionUpdate {
  if (!isRecord(payload) || !isRecord(payload.response)) return { ok: false, error: null };
  const response = payload.response;
  if (response.ok !== true) return { ok: false, error: readExtensionError(response) };
  return { ok: true, state: readExtensionState(response.status), error: readExtensionError(response.status) };
}

/** The bridge is only loaded from UniLogin origins, the page's own origin, or loopback during local development. */
export function canUseAutomaticExtensionBridge(value: string): boolean {
  try {
    const bridge = new URL(value);
    const page = new URL(window.location.href);
    if (bridge.origin === page.origin) return true;
    if (bridge.protocol === 'https:' && UNILOGIN_BRIDGE_ORIGINS.includes(bridge.origin)) return true;
    return (
      bridge.protocol === 'http:' && page.protocol === 'http:' && isLoopbackHost(bridge.hostname) && isLoopbackHost(page.hostname)
    );
  } catch {
    return false;
  }
}

export function detectExtensionBrowser(userAgent: string): ExtensionBrowser | null {
  if (/(?:Android|iPhone|iPad|iPod)/.test(userAgent)) return null;
  if (/Firefox\//.test(userAgent)) return 'firefox';
  if (/Edg\//.test(userAgent)) return 'edge';
  if (/Safari\//.test(userAgent) && !/(?:Chrome|Chromium|CriOS|Edg|OPR)\//.test(userAgent)) return 'safari';
  return /(?:Chrome|Chromium|OPR)\//.test(userAgent) ? 'chrome' : null;
}

function isBridgeResponse(value: unknown, correlationId: string): value is { payload: unknown } {
  if (!isRecord(value)) return false;
  return value.type === 'unilogin:extension-bridge:response' && value.correlationId === correlationId;
}

function readExtensionState(value: unknown): string | null {
  if (typeof value === 'string') return value;
  return isRecord(value) && typeof value.state === 'string' ? value.state : null;
}

function readExtensionError(value: unknown): string | null {
  if (!isRecord(value)) return null;
  if (typeof value.error === 'string') return value.error;
  return isRecord(value.status) && typeof value.status.error === 'string' ? value.status.error : null;
}

function isLoopbackHost(value: string): boolean {
  return LOOPBACK_HOSTNAMES.includes(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
